import {
  type InferOutput,
  array,
  enum_,
  nullable,
  number,
  object,
  string
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import {
  type UploadFileResult,
  uploadFilesSchema
} from '@/features/uploads/models'
import {
  type CreateMonthlyTourReportAdditionFileDto,
  type MonthlyTourReportAdditionExEntity,
  type MonthlyTourReportAdditionFileExEntity,
  type UpdateMonthlyTourReportAdditionDto
} from '@/libs/api'
import {
  type MONTHLY_TOUR_REPORT_ADDITION_FILE_CATEGORY,
  ROUND_TYPE,
  type RoundType,
  TAX_TYPE,
  type TaxType
} from '@/libs/api/types'

/**
 * 月次支払レポートの追加料金・前月調整の追加項目フォームスキーマ
 */
export const monthlyTourReportAdditionItemRowSchema = object({
  /** 追加項目ID */
  additionalPaymentCategoryId: number(VALIDATION_MESSAGES.REQUIRED_ITEM),
  /** 料金 */
  price: number(),
  /**
   * 税区分マスタ ID (VOC_PJ-462 B案、DTO 送信用)。
   * Copilot 指摘 (#3279378720) 対応: master の `taxCategoryId` が null のケースを許容するため nullable 化。
   * null 送信時は BE 側で taxType / カスケードを使って fallback 解決する。
   */
  taxCategoryId: nullable(number()),
  /** 料金 税区分 (DEPRECATED: 移行期 form value display 互換のため保持。taxCategoryId が null の場合は fallback として DTO に送信) */
  taxType: enum_(TAX_TYPE),
  /** 料金 切り上げ・切り捨て */
  roundType: enum_(ROUND_TYPE),
  /** 追加項目 */
  additionalPaymentCategory: object({
    name: string()
  }),
  /** 追加項目 証跡ファイル */
  monthlyTourReportAdditionItemFiles: array(
    object({
      name: string(),
      key: string()
    })
  )
})

/**
 * 月次支払レポートの追加料金・前月調整のフォームスキーマ
 */
export const monthlyTourReportAdditionFormValueSchema = object({
  /** 管理料 */
  managementPrice: nullable(number()),
  /** 管理費 税区分  */
  managementPriceTaxType: enum_(TAX_TYPE),
  /** 管理費 切り上げ・切り捨て */
  managementPriceRoundType: enum_(ROUND_TYPE),
  /** 高速道路料金 */
  highwayPrice: nullable(number()),
  /** 高速道路料金 税区分  */
  highwayPriceTaxType: enum_(TAX_TYPE),
  /** 高速道路料金 切り上げ・切り捨て */
  highwayPriceRoundType: enum_(ROUND_TYPE),
  /** 高速道路料金の添付ファイル */
  highwayPriceFile: array(uploadFilesSchema),
  /** 駐車料金 */
  parkingPrice: nullable(number()),
  /** 駐車料金 税区分  */
  parkingPriceTaxType: enum_(TAX_TYPE),
  /** 駐車料金 切り上げ・切り捨て */
  parkingPriceRoundType: enum_(ROUND_TYPE),
  /** 駐車料金の添付ファイル */
  parkingPriceFile: array(uploadFilesSchema),
  /** その他料金 */
  otherPrice: nullable(number()),
  /** その他料金 税区分  */
  otherPriceTaxType: enum_(TAX_TYPE),
  /** その他料金 切り上げ・切り捨て */
  otherPriceRoundType: enum_(ROUND_TYPE),
  /** その他料金の添付ファイル */
  otherPriceFile: array(uploadFilesSchema),
  /** 前月運賃の調整金 */
  prevMonthChargePrice: nullable(number()),
  /** 前月運賃の調整金 税区分 */
  prevMonthChargePriceTaxType: enum_(TAX_TYPE),
  /** 前月運賃の調整金 切り上げ・切り捨て */
  prevMonthChargePriceRoundType: enum_(ROUND_TYPE),
  /** 前月運賃の調整金の添付ファイル */
  prevMonthChargePriceFile: array(uploadFilesSchema),
  /** 前月料金の調整金 */
  prevMonthPrice: nullable(number()),
  /** 前月料金の調整金 税区分 */
  prevMonthPriceTaxType: enum_(TAX_TYPE),
  /** 前月料金の調整金 切り上げ・切り捨て */
  prevMonthPriceRoundType: enum_(ROUND_TYPE),
  /** 前月料金の調整金の添付ファイル */
  prevMonthPriceFile: array(uploadFilesSchema),
  /** 備考 */
  note: string(),
  /** 追加項目 */
  monthlyTourReportAdditionItems: array(monthlyTourReportAdditionItemRowSchema)
})

/**
 * 月次支払レポートの追加料金・前月調整のフォーム値
 */
export type MonthlyTourReportAdditionFormValue = InferOutput<
  typeof monthlyTourReportAdditionFormValueSchema
>

/**
 * 月次支払レポートの追加料金・前月調整のEntityからフォーム値に変換
 */
export const mapToMonthlyTourReportAdditionFormValue = (
  addition: MonthlyTourReportAdditionExEntity | null
): MonthlyTourReportAdditionFormValue => {
  const files = mapToFileByCategory(
    addition?.monthlyTourReportAdditionFiles ?? []
  )

  return {
    managementPrice: mapToPrice(addition?.managementPrice),
    managementPriceTaxType: mapToTaxType(addition?.managementPriceTaxType),
    managementPriceRoundType: mapToRoundType(
      addition?.managementPriceRoundType
    ),
    highwayPrice: mapToPrice(addition?.highwayPrice),
    highwayPriceTaxType: mapToTaxType(addition?.highwayPriceTaxType),
    highwayPriceRoundType: mapToRoundType(addition?.highwayPriceRoundType),
    highwayPriceFile: files.HIGHWAY_PRICE,
    parkingPrice: mapToPrice(addition?.parkingPrice),
    parkingPriceTaxType: mapToTaxType(addition?.parkingPriceTaxType),
    parkingPriceRoundType: mapToRoundType(addition?.parkingPriceRoundType),
    parkingPriceFile: files.PARKING_PRICE,
    otherPrice: mapToPrice(addition?.otherPrice),
    otherPriceTaxType: mapToTaxType(addition?.otherPriceTaxType),
    otherPriceRoundType: mapToRoundType(addition?.otherPriceRoundType),
    otherPriceFile: files.OTHER_PRICE,
    prevMonthChargePrice: mapToPrice(addition?.prevMonthChargePrice),
    prevMonthChargePriceTaxType: mapToTaxType(
      addition?.prevMonthChargePriceTaxType
    ),
    prevMonthChargePriceRoundType: mapToRoundType(
      addition?.prevMonthChargePriceRoundType
    ),
    prevMonthChargePriceFile: files.PREV_MONTH_CHARGE_PRICE,
    prevMonthPrice: mapToPrice(addition?.prevMonthPrice),
    prevMonthPriceTaxType: mapToTaxType(addition?.prevMonthPriceTaxType),
    prevMonthPriceRoundType: mapToRoundType(addition?.prevMonthPriceRoundType),
    prevMonthPriceFile: files.PREV_MONTH_PRICE,
    note: addition?.note ?? '',
    monthlyTourReportAdditionItems:
      // VOC_PJ-462 #1856 PR2 で API entity (MonthlyTourReportAdditionItemExEntity) から `taxType`
      // が外れた (`taxCategoryId` 化) ため、form value schema 要求の `taxType` を
      // `additionalPaymentCategory.taxType` から fallback で注入する。
      addition?.monthlyTourReportAdditionItems?.map((item) => ({
        ...item,
        taxType: item.additionalPaymentCategory.taxType,
        monthlyTourReportAdditionItemFiles:
          item.monthlyTourReportAdditionItemFiles?.map((file) => ({
            name: file.fileName,
            key: file.fileKey
          })) || []
      })) || []
  }
}

const mapToPrice = (price: number | null | undefined): number | null =>
  price ?? null

const mapToTaxType = (taxType: TaxType | null | undefined): TaxType =>
  taxType ?? TAX_TYPE.NON_TAXABLE

const mapToRoundType = (roundType: RoundType | null | undefined): RoundType =>
  roundType ?? ROUND_TYPE.ROUND_UP

type FileByCategory = {
  [key in keyof typeof MONTHLY_TOUR_REPORT_ADDITION_FILE_CATEGORY]: UploadFileResult[]
}

/**
 * 月次支払レポートの追加料金・前月調整のファイルをカテゴリ別に分類
 */
const mapToFileByCategory = (
  files: MonthlyTourReportAdditionFileExEntity[]
): FileByCategory => {
  const fileByCategory: FileByCategory = {
    MANAGEMENT_PRICE: [],
    HIGHWAY_PRICE: [],
    PARKING_PRICE: [],
    OTHER_PRICE: [],
    PREV_MONTH_CHARGE_PRICE: [],
    PREV_MONTH_PRICE: []
  }

  files.forEach((file) => {
    const category = file.category
    if (category) {
      fileByCategory[category].push({
        key: file.monthlyTourReportAdditionFileKey,
        name: file.monthlyTourReportAdditionFileName
      })
    }
  })

  return fileByCategory
}

/**
 * 月次支払レポートの追加料金・前月調整のフォーム値から更新用DTOに変換
 */
export const mapToUpdateMonthlyTourReportAdditionDto = (
  formValue: MonthlyTourReportAdditionFormValue
): UpdateMonthlyTourReportAdditionDto => {
  const {
    highwayPriceFile,
    parkingPriceFile,
    otherPriceFile,
    prevMonthChargePriceFile,
    prevMonthPriceFile,
    monthlyTourReportAdditionItems,
    ...rest
  } = formValue
  return {
    ...rest,
    monthlyTourReportAdditionFiles: [
      ...mapToUpdateMonthlyTourReportAdditionFileDto(
        filterNullFiles(highwayPriceFile),
        'HIGHWAY_PRICE',
        rest.highwayPrice
      ),
      ...mapToUpdateMonthlyTourReportAdditionFileDto(
        filterNullFiles(parkingPriceFile),
        'PARKING_PRICE',
        rest.parkingPrice
      ),
      ...mapToUpdateMonthlyTourReportAdditionFileDto(
        filterNullFiles(otherPriceFile),
        'OTHER_PRICE',
        rest.otherPrice
      ),
      ...mapToUpdateMonthlyTourReportAdditionFileDto(
        filterNullFiles(prevMonthChargePriceFile),
        'PREV_MONTH_CHARGE_PRICE',
        rest.prevMonthChargePrice
      ),
      ...mapToUpdateMonthlyTourReportAdditionFileDto(
        filterNullFiles(prevMonthPriceFile),
        'PREV_MONTH_PRICE',
        rest.prevMonthPrice
      )
    ],
    monthlyTourReportAdditionItems: monthlyTourReportAdditionItems.map(
      (item) => ({
        price: item.price,
        // VOC_PJ-462 #1856 PR2 で BE DTO が `taxCategoryId` (optional) に移行済。
        // taxCategoryId が null（カテゴリマスタ未設定）のときは taxType を fallback 送信し、
        // BE 側の resolveTaxCategoryId で '課税10%' / '非課税' TaxCategory に解決させる。
        ...(item.taxCategoryId != null
          ? { taxCategoryId: item.taxCategoryId }
          : { taxType: item.taxType }),
        roundType: item.roundType,
        additionalPaymentCategoryId: item.additionalPaymentCategoryId,
        monthlyTourReportAdditionItemFiles:
          item.monthlyTourReportAdditionItemFiles.map((file) => ({
            name: file.name,
            key: file.key
          }))
      })
    )
  }
}

/**
 * nullを除外（エラーの場合のみ起こる想定）
 */
const filterNullFiles = (
  files: (UploadFileResult | null)[]
): UploadFileResult[] => {
  return files.filter((file): file is UploadFileResult => file !== null)
}

/**
 * 各項目のファイルをカテゴリごとDTOに変換
 * 項目の料金が 0 or null の場合、空の配列を返す
 */
const mapToUpdateMonthlyTourReportAdditionFileDto = (
  files: UploadFileResult[],
  category: keyof typeof MONTHLY_TOUR_REPORT_ADDITION_FILE_CATEGORY,
  price: number | null
): CreateMonthlyTourReportAdditionFileDto[] => {
  if (price == null || price == 0) return []

  return files.map((file) => ({
    key: file.key,
    name: file.name,
    category
  }))
}

/**
 * 指定した金額が月次支払レポートの追加料金・前月調整として設定されているかを判定
 */
const hasMonthlyTourReportAdditionPrice = (price: number | null): boolean =>
  price !== null && price !== 0

/**
 * 月次支払レポートの項目が必須かどうかを判定
 */
export const requireMonthlyTourReportAdditionItems = ({
  highwayPrice,
  parkingPrice,
  otherPrice,
  prevMonthChargePrice,
  prevMonthPrice
}: Pick<
  MonthlyTourReportAdditionFormValue,
  | 'highwayPrice'
  | 'parkingPrice'
  | 'otherPrice'
  | 'prevMonthChargePrice'
  | 'prevMonthPrice'
>): {
  highwayPriceFile: boolean
  parkingPriceFile: boolean
  otherPriceFile: boolean
  prevMonthChargePriceFile: boolean
  prevMonthPriceFile: boolean
  note: boolean
} => {
  const highwayPriceFile = hasMonthlyTourReportAdditionPrice(highwayPrice)
  const parkingPriceFile = hasMonthlyTourReportAdditionPrice(parkingPrice)
  const otherPriceFile = hasMonthlyTourReportAdditionPrice(otherPrice)
  const prevMonthChargePriceFile =
    hasMonthlyTourReportAdditionPrice(prevMonthChargePrice)
  const prevMonthPriceFile = hasMonthlyTourReportAdditionPrice(prevMonthPrice)

  return {
    highwayPriceFile,
    parkingPriceFile,
    otherPriceFile,
    prevMonthChargePriceFile,
    prevMonthPriceFile,
    note: false
  }
}

export type monthlyTourReportAdditionItemFormValue = InferOutput<
  typeof monthlyTourReportAdditionItemRowSchema
>
export const initialMonthlyTourReportAdditionItemFormValue: monthlyTourReportAdditionItemFormValue =
  {
    additionalPaymentCategoryId: 0,
    price: 0,
    taxCategoryId: null,
    taxType: TAX_TYPE.TAXABLE,
    roundType: ROUND_TYPE.ROUND_UP,
    monthlyTourReportAdditionItemFiles: [],
    additionalPaymentCategory: { name: '' }
  }
